import { env } from "@/env";
import { trpc } from "@/lib/trpc";
import { toError } from "@repo/lib/utils";
import { Button } from "@repo/ui/components/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/Card";
import { toast } from "@repo/ui/lib/toast";
import { ExternalLink, Loader, Play, RotateCw, Square } from "lucide-react";

export const Metabase = () => {
  const {
    data: status,
    isLoading,
    refetch,
  } = trpc.metabase.status.useQuery(undefined, {
    refetchInterval: 5000, // Check status every 5 seconds
  });

  const startMutation = trpc.metabase.start.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      void refetch();
    },
    onError: (error) => {
      toast.error(toError(error).message);
    },
  });

  const stopMutation = trpc.metabase.stop.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      void refetch();
    },
    onError: (error) => {
      toast.error(toError(error).message);
    },
  });

  const restartMutation = trpc.metabase.restart.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      void refetch();
    },
    onError: (error) => {
      toast.error(toError(error).message);
    },
  });

  const isAnyPending =
    startMutation.isPending || stopMutation.isPending || restartMutation.isPending;
  const isRunning = status?.running ?? false;

  return (
    <div className="flex flex-1 flex-col gap-6 p-8">
      <div>
        <h1 className="text-3xl font-bold">Analytics Browser</h1>
        <p className="text-muted-foreground">Explore analytics data with Metabase</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Metabase Control</CardTitle>
          <CardDescription>
            Start and stop Metabase to manage server resources.
            <div className="text-destructive">
              This uses ~512MB RAM when running so please Stop it when you are done using it!
            </div>
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="flex flex-col gap-4">
            {/* Status */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div
                  className={`h-3 w-3 rounded-full ${
                    isLoading
                      ? "bg-muted animate-pulse"
                      : isRunning
                        ? "bg-green-500"
                        : "bg-gray-400"
                  }`}
                />
                <span className="font-medium">
                  {isLoading ? "Checking..." : isRunning ? "Running" : "Stopped"}
                </span>
              </div>
              {status?.startedAt && isRunning && (
                <span className="text-muted-foreground text-sm">
                  Started {new Date(status.startedAt).toLocaleString()}
                </span>
              )}
            </div>

            {/* Controls */}
            <div className="flex flex-wrap gap-2">
              {isRunning ? (
                <>
                  <Button
                    variant="default"
                    onClick={() => window.open(env.VITE_METABASE_URL, "_blank")}
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open Metabase
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => restartMutation.mutate()}
                    disabled={isAnyPending}
                  >
                    {restartMutation.isPending ? (
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCw className="mr-2 h-4 w-4" />
                    )}
                    Restart
                  </Button>
                  <Button
                    variant="outline"
                    color="destructive"
                    onClick={() => stopMutation.mutate()}
                    disabled={isAnyPending}
                  >
                    {stopMutation.isPending ? (
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Square className="mr-2 h-4 w-4" />
                    )}
                    Stop
                  </Button>
                </>
              ) : (
                <Button
                  variant="default"
                  onClick={() => startMutation.mutate()}
                  disabled={isAnyPending}
                >
                  {startMutation.isPending ? (
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="mr-2 h-4 w-4" />
                  )}
                  Start Metabase
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
